'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { CheckCircle } from 'lucide-react';

const trustBadges = [
  '14-day free trial',
];

export default function CTASection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-gradient-to-br from-primary to-primary-light py-16 sm:py-20 lg:py-24"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_60%)]" />

      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <h2
          className={`text-2xl sm:text-3xl lg:text-4xl font-700 text-white leading-tight transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Ready to Transform Your Payment Operations?
        </h2>

        <p
          className={`mt-4 text-base sm:text-lg text-white/85 max-w-2xl mx-auto transition-all duration-700 delay-150 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Join hundreds of organizations already using Meristem Digital to streamline their field payments.
        </p>

        <div
          className={`mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Link
            href="/login-screen"
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 rounded-lg bg-white text-primary font-600 text-sm sm:text-base shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
          >
            Start Free Trial
          </Link>
          <Link
            href="#contact"
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 rounded-lg border-2 border-white text-white font-600 text-sm sm:text-base hover:bg-white/10 active:scale-[0.98] transition-all duration-200"
          >
            Contact Sales
          </Link>
        </div>

        <div
          className={`mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {trustBadges.map((badge) => (
            <div key={badge} className="flex items-center gap-1.5 text-white/80 text-xs sm:text-sm">
              <CheckCircle size={14} className="text-white/90 flex-shrink-0" />
              <span>{badge}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
